import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { FileUploadService } from '../api-services/file-upload.service';
import { Certification } from '../data-models/certification.model';
import { CertificationCreate } from '../request-models/certification-create.model';
import { CertificationUpdate } from '../request-models/certification-update.model';
import { CertificationRequirementStatus } from '../response-models/certification-requirement-status.model';
import { CertificationUploadUrl } from '../response-models/certification-upload-url.model';
import { toCertificationCreateRequest, toCertificationUpdateRequest } from '../utils/certification-request-utils';
import { isRecord } from '../utils/is-record';
import { nullableString, stringArrayOrEmpty } from '../utils/wire-value-utils';
import { CertificationType } from '../data-models/certification-type.model';

/**
 * Certifications of the signed-in user's profile or an organization: `/certifications[/:id[/renew|/history|
 * /download-url]]`, `/certifications/profile` and `/certifications/organization/:id[/status]`
 * (hub `user-certifications-controller.ts`).
 *
 * Every method rejects with an `ApiError` (whose `message` is the hub's error); 403 means the user may not
 * change that certification.
 */
@Injectable({
  providedIn: 'root'
})
export class CertificationService extends BaseService<Certification> {
  private readonly fileUploads = inject(FileUploadService);

  constructor() {
    super('certifications', Certification);
  }

  /**
   * Get certifications for an organization
   */
  async getOrganizationCertifications(organizationId: string): Promise<Certification[]> {
    return firstValueFrom(this.getMany({ path: ['organization', organizationId] }));
  }

  /**
   * Get certifications for current user's profile
   */
  async getProfileCertifications(): Promise<Certification[]> {
    return firstValueFrom(this.getMany({ path: 'profile' }));
  }

  /**
   * Get a specific certification by ID
   */
  async getCertification(id: string): Promise<Certification | null> {
    return firstValueFrom(this.get(id));
  }

  /**
   * Create a new certification
   */
  async createCertification(data: CertificationCreate): Promise<Certification> {
    return requireCertification(await firstValueFrom(this.create(toCertificationCreateRequest(data))));
  }

  /**
   * Update a certification. Send `null` for an optional field the user emptied.
   */
  async updateCertification(id: string, data: CertificationUpdate): Promise<Certification> {
    return requireCertification(await firstValueFrom(this.update(id, toCertificationUpdateRequest(data))));
  }

  /**
   * Delete (archive) a certification
   */
  async deleteCertification(id: string): Promise<void> {
    await firstValueFrom(this.delete(id));
  }

  /**
   * Renew a certification: the hub archives it and creates the renewal.
   */
  async renewCertification(id: string, data: CertificationCreate): Promise<Certification> {
    const body = toCertificationCreateRequest(data);
    const renewed = await firstValueFrom(this.request('POST', { id, path: 'renew' }, d => this.toCertification(d), body));
    return requireCertification(renewed);
  }

  /**
   * Get renewal history for a certification
   */
  async getCertificationHistory(id: string): Promise<Certification[]> {
    const read = (data: unknown) => (Array.isArray(data) ? data.map(item => this.createType(item)) : []);
    return firstValueFrom(this.request('GET', { id, path: 'history' }, read));
  }

  /**
   * Get certification status for an organization
   */
  async getOrganizationCertificationStatus(organizationId: string): Promise<CertificationRequirementStatus> {
    return firstValueFrom(this.request('GET', { path: ['organization', organizationId, 'status'] }, toRequirementStatus));
  }

  /** Get a pre-signed URL for uploading a certification document (via `FileUploadService`, which owns `/file-uploads`). */
  getUploadUrl(filename: string): Promise<CertificationUploadUrl> {
    return this.fileUploads.getCertificationUploadUrl(filename);
  }

  /**
   * Upload a file to S3 using a pre-signed URL
   */
  async uploadFile(signedUrl: string, file: File): Promise<void> {
    await this.fileUploads.uploadToSignedUrl(signedUrl, file);
  }

  /**
   * Upload a certification document
   * Combines getting the signed URL and uploading the file
   */
  async uploadCertificationDocument(file: File): Promise<{ url: string; fileName: string }> {
    const { uploadUrl, fileKey } = await this.getUploadUrl(file.name);
    await this.uploadFile(uploadUrl, file);
    return {
      url: fileKey,
      fileName: file.name
    };
  }

  /**
   * Get a pre-signed download URL for a certification document
   */
  async getDownloadUrl(certificationId: string): Promise<string> {
    const read = (data: unknown) => (isRecord(data) ? nullableString(data['downloadUrl']) ?? '' : '');
    return firstValueFrom(this.request('GET', { id: certificationId, path: 'download-url' }, read));
  }

  private toCertification(data: unknown): Certification | null {
    return data === null || data === undefined ? null : this.createType(data);
  }
}

/** Create, update and renew always return the certification; a body without one is a hub fault. */
function requireCertification(certification: Certification | null): Certification {
  if (!certification) {
    throw new ApiError(0, [], { code: 'CLIENT_ERROR' }, 'The server did not return the certification');
  }
  return certification;
}

function toRequirementStatus(data: unknown): CertificationRequirementStatus {
  const source = isRecord(data) ? data : {};
  return {
    hasAll: source['hasAll'] === true,
    missing: stringArrayOrEmpty(source['missing']) as CertificationType[],
  };
}
